// ============================================================================
// GumaHUB Input Component - Enterprise Design
// ============================================================================

import React, { forwardRef } from 'react';
import { cn } from '../utils/index.js';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
  variant?: 'default' | 'filled';
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      helperText,
      leftIcon,
      rightIcon,
      fullWidth = false,
      variant = 'default',
      className,
      id,
      ...props
    },
    ref
  ) => {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');

    const variantStyles = {
      default: `
        bg-white dark:bg-[#2d2d2d] border-2 border-slate-200 dark:border-[#3c4043]
        focus:border-guma-500 dark:focus:border-[#8ab4f8] focus:ring-4 focus:ring-guma-500/10 dark:focus:ring-[#8ab4f8]/20
        hover:border-slate-300 dark:hover:border-[#5f6368]
      `,
      filled: `
        bg-slate-50 dark:bg-[#1f1f1f] border-2 border-transparent
        focus:bg-white dark:focus:bg-[#2d2d2d] focus:border-guma-500 dark:focus:border-[#8ab4f8] focus:ring-4 focus:ring-guma-500/10 dark:focus:ring-[#8ab4f8]/20
        hover:bg-slate-100 dark:hover:bg-[#3c3c3c]
      `,
    };

    return (
      <div className={cn('mb-4', fullWidth && 'w-full')}>
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-semibold text-slate-700 dark:text-[#e8eaed] mb-2"
          >
            {label}
            {props.required && <span className="text-guma-500 dark:text-[#ea8685] ml-1">*</span>}
          </label>
        )}
        <div className="relative">
          {leftIcon && (
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400 dark:text-[#9aa0a6]">
              {leftIcon}
            </div>
          )}
          <input
            ref={ref}
            id={inputId}
            className={cn(
              // Base styles
              'block rounded-xl px-4 py-3',
              'text-slate-900 dark:text-[#e8eaed] placeholder-slate-400 dark:placeholder-[#5f6368]',
              'transition-all duration-200',
              'focus:outline-none',
              'disabled:bg-slate-100 dark:disabled:bg-[#1f1f1f] disabled:text-slate-500 dark:disabled:text-[#5f6368] disabled:cursor-not-allowed',
              // Variant styles
              variantStyles[variant],
              // Icon padding
              leftIcon ? 'pl-12' : '',
              rightIcon ? 'pr-12' : '',
              // Error state
              error && 'border-red-400 focus:border-red-500 focus:ring-red-500/10',
              // Width
              fullWidth && 'w-full',
              className
            )}
            {...props}
          />
          {rightIcon && (
            <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none text-slate-400 dark:text-[#9aa0a6]">
              {rightIcon}
            </div>
          )}
        </div>
        {(error || helperText) && (
          <p
            className={cn(
              'mt-2 text-sm flex items-center gap-1.5',
              error ? 'text-red-600 dark:text-[#ea8685]' : 'text-slate-500 dark:text-[#9aa0a6]'
            )}
          >
            {error && (
              <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            )}
            {error || helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

// Textarea variant
export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  helperText?: string;
  fullWidth?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      error,
      helperText,
      fullWidth = false,
      className,
      id,
      ...props
    },
    ref
  ) => {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');

    return (
      <div className={cn('mb-4', fullWidth && 'w-full')}>
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-semibold text-slate-700 dark:text-[#e8eaed] mb-2"
          >
            {label}
            {props.required && <span className="text-guma-500 dark:text-[#ea8685] ml-1">*</span>}
          </label>
        )}
        <textarea
          ref={ref}
          id={inputId}
          className={cn(
            'block rounded-xl px-4 py-3',
            'bg-white dark:bg-[#2d2d2d] border-2 border-slate-200 dark:border-[#3c4043]',
            'text-slate-900 dark:text-[#e8eaed] placeholder-slate-400 dark:placeholder-[#5f6368]',
            'transition-all duration-200',
            'focus:outline-none focus:border-guma-500 dark:focus:border-[#8ab4f8] focus:ring-4 focus:ring-guma-500/10 dark:focus:ring-[#8ab4f8]/20',
            'hover:border-slate-300 dark:hover:border-[#5f6368]',
            'disabled:bg-slate-100 dark:disabled:bg-[#1f1f1f] disabled:text-slate-500 dark:disabled:text-[#5f6368] disabled:cursor-not-allowed',
            'resize-none',
            error && 'border-red-400 dark:border-[#ea8685] focus:border-red-500 dark:focus:border-[#ea8685] focus:ring-red-500/10 dark:focus:ring-[#ea8685]/20',
            fullWidth && 'w-full',
            className
          )}
          {...props}
        />
        {(error || helperText) && (
          <p
            className={cn(
              'mt-2 text-sm flex items-center gap-1.5',
              error ? 'text-red-600 dark:text-[#ea8685]' : 'text-slate-500 dark:text-[#9aa0a6]'
            )}
          >
            {error && (
              <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            )}
            {error || helperText}
          </p>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';

export default Input;
